import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

export const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    if (openItems.includes(index)) {
      setOpenItems(openItems.filter(item => item !== index));
    } else {
      setOpenItems([...openItems, index]);
    }
  };

  const faqItems: FAQItem[] = [
    {
      question: "How does Chinese Food Maps work?",
      answer: "Chinese Food Maps uses advanced location technology to find authentic Chinese restaurants within 10 miles of your location. Simply enter your address or use the 'Use My Location' feature, and we'll show you detailed information about nearby Chinese restaurants, including their specialties, ratings, contact information, and directions. You can sort results by distance or rating to find exactly what you're craving."
    },
    {
      question: "What types of Chinese restaurants can I find?",
      answer: "We list all types of Chinese restaurants, representing the eight major culinary traditions: Cantonese, Sichuan, Jiangsu, Zhejiang, Fujian, Hunan, Anhui, and Shandong cuisine. You'll find everything from dim sum specialists to hot pot restaurants, casual noodle shops to fine dining establishments. Each listing includes details about the restaurant's regional specialties and signature dishes."
    },
    {
      question: "How current is your restaurant information?",
      answer: "We strive to maintain up-to-date information through regular database updates and community feedback. However, restaurant details can change frequently, so we recommend calling ahead to confirm current hours, prices, and availability, especially during holidays or special events. If you notice any outdated information, please use our Contact page to let us know."
    },
    {
      question: "What makes a restaurant authentic Chinese cuisine?",
      answer: "Authentic Chinese restaurants typically feature regional specialties, traditional cooking methods, and proper ingredient combinations following Chinese culinary principles. We look for restaurants that maintain traditional flavors while adapting to local ingredients, proper use of wok hei (breath of the wok), and adherence to regional cooking techniques."
    },
    {
      question: "How do you handle dietary restrictions and preferences?",
      answer: "Many Chinese restaurants offer extensive vegetarian and vegan options, and we clearly mark these in our listings. We also indicate whether restaurants can accommodate common dietary restrictions like gluten-free requirements. Each restaurant listing includes information about spice levels and whether they can modify dishes to meet specific dietary needs."
    },
    {
      question: "Why can't I find my favorite Chinese restaurant listed?",
      answer: "While we strive to maintain a comprehensive database, some restaurants might not be listed yet, or they might be just outside our 10-mile search radius. If you know of a great Chinese restaurant that's not on our platform, please let us know through our Contact page. We're always excited to add new authentic Chinese restaurants to our database."
    },
    {
      question: "Is my location data safe with Chinese Food Maps?",
      answer: "Absolutely! We take your privacy seriously and follow strict data protection protocols. We only use your location data to find nearby Chinese restaurants - that's it. We don't store, sell, or share your location information with third parties. If you prefer not to share your location, you can always manually enter an address or zip code to search."
    },
    {
      question: "Can I order food through Chinese Food Maps?",
      answer: "Currently, we don't handle food orders directly. However, we make it easy to connect with restaurants - just tap the 'Call Now' button to place an order, or use the 'Get Directions' feature to visit the restaurant. We also provide links to restaurants' online ordering systems when available."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Frequently Asked Questions | Chinese Food Maps</title>
        <meta name="description" content="Get answers to common questions about using Chinese Food Maps, from finding the best local Chinese restaurants to understanding our ratings and reviews system." />
        <meta property="og:title" content="Frequently Asked Questions | Chinese Food Maps" />
        <meta property="og:description" content="Get answers to common questions about using Chinese Food Maps, from finding the best local Chinese restaurants to understanding our ratings and reviews system." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chinesefoodmaps.com/faq" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/faq" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Frequently Asked Questions</h1>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                <button
                  className="flex justify-between items-center w-full text-left py-2 focus:outline-none"
                  onClick={() => toggleItem(index)}
                >
                  <h3 className="text-lg font-medium text-red-800">{item.question}</h3>
                  {openItems.includes(index) ? (
                    <ChevronUp className="text-red-700 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="text-red-700 flex-shrink-0" />
                  )}
                </button>
                
                <div 
                  className={`mt-2 text-gray-600 transition-all duration-300 overflow-hidden ${
                    openItems.includes(index) ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="text-lg leading-relaxed">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};